import React, { useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, DrawerHeaderButton, DrawerLayout } from '@/shared/ui';

import { Permission } from '@/entities/permissions';
import PremiumFeatureMessage from '@/features/_shared/UpgradeToPremium/PremiumFeatureMessage';
import { usePermission } from '@/features/_shared/user';
import type { AppCatalogEntry } from '@/features/dashboard/components/apps';
import { APP_CATALOG } from '@/features/dashboard/components/apps';
import { Category } from '@/features/dashboard/components/apps/types';
import { DrawerFooterButtons } from '@/features/dashboard/components/Drawer/components/DrawerFooterButtons';
import { useDrawerSteps } from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { DrawerHeader } from '@/features/dashboard/components/Drawer/DrawerHeader';
import SelectWidget from '@/features/dashboard/components/SelectWidget';
import { clearSelection, setPendingAppSymbols, SELECTOR_MODES } from '@/features/symbol-selector';
import Button from '@/shared/ui/primitives/Button';
import Typography from '@/shared/ui/typography/Text';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

const categories = [
  { key: Category.All, label: 'All apps' },
  { key: Category.Markets, label: 'Markets' },
  { key: Category.Charts, label: 'Charts' },
  { key: Category.Data, label: 'Data' }
];

// Lazy init to avoid issues with circular module deps at load time
let SPOT_INCOMPATIBLE_IDS: Set<string> | null = null;
let FWD_INCOMPATIBLE_IDS: Set<string> | null = null;

function getSpotIncompatibleIds(): Set<string> {
  SPOT_INCOMPATIBLE_IDS ??= new Set(
    APP_CATALOG.filter(a => (a.incompatibleWith as string[]).includes('spot')).map(a => a.id)
  );
  return SPOT_INCOMPATIBLE_IDS;
}
function getFwdIncompatibleIds(): Set<string> {
  FWD_INCOMPATIBLE_IDS ??= new Set(
    APP_CATALOG.filter(a => (a.incompatibleWith as string[]).includes('fwd')).map(a => a.id)
  );
  return FWD_INCOMPATIBLE_IDS;
}

export const AppSelector = () => {
  const { setDrawerStep, closeDrawer } = useDrawerSteps();
  const [category, setCategory] = useState<Category>(Category.All);
  const dispatch = useAppDispatch();
  const selectorMode = useAppSelector(s => s.symbolSelector.selectorMode);
  const selectorSymbols = useAppSelector(s => s.symbolSelector.symbols);

  const { hasPermission } = usePermission();

  // "From selector" mode: the drawer was opened via the "Create app" button
  const isFromSelector = selectorSymbols.length > 0;

  const filteredApps = useMemo(() => {
    if (category === Category.All) {
      return APP_CATALOG;
    }
    return APP_CATALOG.filter(app => app.category === category);
  }, [category]);

  const isAppDisabledByMode = useMemo(() => {
    if (!isFromSelector) return (_id: string) => false;
    if (selectorMode === SELECTOR_MODES.SPOT) {
      return (id: string) => getSpotIncompatibleIds().has(id);
    }
    if (selectorMode === SELECTOR_MODES.FORWARD_CURVE) {
      return (id: string) => getFwdIncompatibleIds().has(id);
    }
    return (_id: string) => false;
  }, [isFromSelector, selectorMode]);

  const [availableApps, premiumOnlyApps] = useMemo(() => {
    if (hasPermission(Permission.DashboardPremiumWidgets)) {
      return [filteredApps, [] as AppCatalogEntry[]];
    }
    const nonPremium = filteredApps.filter(app => !app.premium);
    const premium = filteredApps.filter(app => app.premium);
    return [nonPremium, premium];
  }, [filteredApps, hasPermission]);

  const handleSelectApp = (app: AppCatalogEntry) => {
    if (isFromSelector) {
      dispatch(setPendingAppSymbols(selectorSymbols));
      dispatch(clearSelection());
    }
    setDrawerStep(app.step);
  };

  return (
    <DrawerLayout
      footer={
        <DrawerFooterButtons
          disabled
          onDiscard={() => {}}
          onSave={() => {}}
        />
      }
      header={
        <DrawerHeader
          before={
            <DrawerHeaderButton
              label='Go back'
              onClick={closeDrawer}
            >
              <ArrowBackIcon />
            </DrawerHeaderButton>
          }
          buttons={null}
          title='Add new app'
        />
      }
      subheader={
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: 3 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '20px' }}>Choose app type</Typography>
            <Typography
              color='gx.subtext'
              sx={{ fontWeight: 500, fontSize: '14px' }}
            >
              Select an app to start configuring
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(4,1fr)' }}>
            {categories.map(({ key, label }) => (
              <Button
                key={key}
                color={category === key ? 'primary' : 'secondary'}
                size='small'
                sx={{
                  width: 'auto',
                  maxWidth: '100%',
                  height: 36
                }}
                variant='contained'
                onClick={() => {
                  setCategory(key);
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {premiumOnlyApps.length > 0 && (
          <Box sx={{ position: 'relative', overflow: 'hidden', userSelect: 'none' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                position: 'relative',
                filter: 'blur(3px)'
              }}
            >
              {premiumOnlyApps.map(app => (
                <SelectWidget
                  key={app.id}
                  badge={app.badge}
                  description={app.description}
                  icon={app.icon}
                  soon={app.soon}
                  testId={`select-widget-${app.id}`}
                  title={app.title}
                  onSelect={() => {}}
                />
              ))}
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                background: 'rgba(255, 255, 255, 0.05)',
                cursor: 'default',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                color: '#FFF'
              }}
            >
              <PremiumFeatureMessage />
            </Box>
          </Box>
        )}
        {availableApps.map(app => (
          <SelectWidget
            key={app.id}
            badge={app.badge}
            description={app.description}
            disabled={isAppDisabledByMode(app.id)}
            icon={app.icon}
            soon={app.soon}
            testId={`select-widget-${app.id}`}
            title={app.title}
            onSelect={() => {
              handleSelectApp(app);
            }}
          />
        ))}
      </Box>
    </DrawerLayout>
  );
};
